import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Smartphone, KeyRound, ShieldCheck, Loader2, CheckCircle2, MessageCircle,
  Sparkles, LogOut, Clock, ChevronRight, ClipboardPaste, QrCode, Monitor,
  Trash2, Plus, Minus, Search, X, UserPlus,
} from "lucide-react";
import Layout from "@/components/Layout";
import PaywallModal from "@/components/PaywallModal";
import { api, vibrate } from "@/lib/api";

const STEPS = [
  "Ouvre WhatsApp sur ton téléphone",
  "Paramètres → Appareils liés",
  "Lier un appareil → Lier avec numéro de téléphone",
  "Tape le code ci-dessus (valable 3 min)",
];

export default function WhatsAppImport() {
  const navigate = useNavigate();
  const isMobile = typeof navigator !== "undefined" && /iPad|iPhone|iPod|Android/i.test(navigator.userAgent);
  const [view, setView] = useState("loading"); // loading|none|pending|qr|connected|expired|soon
  const [method, setMethod] = useState(isMobile ? "code" : "qr"); // code|qr
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState(null);
  const [qrUrl, setQrUrl] = useState(null);
  const [session, setSession] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [maxTracked, setMaxTracked] = useState(5);
  const [trackedCount, setTrackedCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const [paywall, setPaywall] = useState(null);
  const [importing, setImporting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [chats, setChats] = useState([]);
  const [chatsLoading, setChatsLoading] = useState(false);
  const [chatQuery, setChatQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [pickerFilter, setPickerFilter] = useState("all");
  const searchTimer = useRef(null);
  const [trackingJid, setTrackingJid] = useState(null);
  const poll = useRef(null);
  const importTimer = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/whatsapp/session/status");
      setSession(data);
      if (data.status === "connected") { setView("connected"); loadContacts(); }
      else if (data.status === "pending") setView("pending");
      else if (data.status === "expired") setView("expired");
      else setView("none");
    } catch (e) { setView("none"); }
  }, []);

  const loadContacts = async () => {
    try {
      const { data } = await api.get("/whatsapp/contacts");
      setContacts(data.contacts || []);
      setMaxTracked(data.max_tracked || 5);
      setTrackedCount(data.tracked_count || 0);
    } catch (e) {}
  };

  // Baileys pushes ~3-7 days of history via message webhooks right after connect.
  // Show an import window that keeps refreshing contacts as batches land.
  const startImportWindow = () => {
    setImporting(true);
    clearInterval(importTimer.current);
    let ticks = 0;
    importTimer.current = setInterval(() => {
      ticks += 1;
      loadContacts();
      if (ticks >= 7) { clearInterval(importTimer.current); setImporting(false); }
    }, 3000);
  };

  const goConnected = (data) => {
    vibrate([100, 50, 100]);
    clearInterval(poll.current);
    setSession(data); setView("connected"); loadContacts(); startImportWindow();
  };

  const untrackContact = async (rid) => {
    vibrate();
    try { await api.post(`/whatsapp/untrack/${rid}`); loadContacts(); toast.success("Retiré du suivi (gardé dans l'historique)"); }
    catch (e) { toast.error("Action impossible"); }
  };

  useEffect(() => { loadStatus(); return () => clearInterval(poll.current); }, [loadStatus]);

  useEffect(() => {
    if (view === "pending" || view === "qr") {
      poll.current = setInterval(async () => {
        try {
          const { data } = await api.get("/whatsapp/session/status");
          if (view === "qr" && data.qr_data_url) setQrUrl(data.qr_data_url);
          if (data.status === "connected") goConnected(data);
        } catch (e) {}
      }, 2000);
      return () => clearInterval(poll.current);
    }
  }, [view]);

  useEffect(() => () => clearInterval(importTimer.current), []);

  const getCode = async () => {
    if (!phone.trim()) { toast.error("Entre ton numéro WhatsApp"); return; }
    vibrate(); setBusy(true);
    try {
      const { data } = await api.post("/whatsapp/pairing-code", { phone_number: phone.trim() });
      if (data.status === "connected") {
        vibrate([100, 50, 100]);
        setSession(data); setView("connected"); loadContacts();
        toast.success("WhatsApp déjà connecté");
        return;
      }
      setCode(data.code); setView("pending");
      toast.success("Code généré, associe ton WhatsApp");
    } catch (e) {
      handleConnectError(e);
    } finally { setBusy(false); }
  };

  const startQr = async () => {
    vibrate(); setBusy(true); setQrUrl(null);
    try {
      await api.post("/whatsapp/qr-session");
      setView("qr");
      toast.success("Scanne le QR avec ton téléphone");
    } catch (e) {
      handleConnectError(e);
    } finally { setBusy(false); }
  };

  const handleConnectError = (e) => {
    const st = e?.response?.status;
    const detail = e?.response?.data?.detail;
    if (st === 402 && detail?.error === "quota_exceeded") setPaywall(detail);
    else if (st === 501) setView("soon");
    else toast.error("Service WhatsApp momentanément indisponible");
  };

  const disconnect = async () => {
    if (!window.confirm("Déconnecter ton WhatsApp de DocLove ?")) return;
    vibrate(); setBusy(true);
    try { await api.post("/whatsapp/session/disconnect"); setView("none"); setSession(null); setContacts([]); setCode(null); }
    finally { setBusy(false); }
  };

  const analyzeContact = async (rid) => {
    vibrate(); setAnalyzing(rid);
    try {
      await api.post(`/whatsapp/analyze/${rid}`);
      toast.success("Relation mise à jour");
      loadContacts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analyse impossible");
    } finally { setAnalyzing(null); }
  };

  const openPicker = async () => {
    vibrate(); setPickerOpen(true); setChatsLoading(true); setChatQuery(""); setSearchResults(null); setPickerFilter("all");
    try {
      const { data } = await api.get("/whatsapp/chats", { params: { limit: 300 } });
      setChats(data.chats || []);
    } catch (e) {
      toast.error("Impossible de charger tes conversations WhatsApp");
      setPickerOpen(false);
    } finally { setChatsLoading(false); }
  };

  const pickChat = async (chat) => {
    if (trackedCount >= maxTracked) {
      toast.error(`Tu suis déjà ${maxTracked} contacts. Retires-en un d'abord.`);
      return;
    }
    vibrate(); setTrackingJid(chat.jid);
    try {
      const { data } = await api.post("/whatsapp/track-chat", { jid: chat.jid, phone: chat.phone, name: chat.name });
      toast.success(data.analyzed ? `${chat.name} suivi et analysé` : `${chat.name} est maintenant suivi`);
      setPickerOpen(false);
      loadContacts();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : "Impossible de suivre ce contact");
    } finally { setTrackingJid(null); }
  };

  // Debounced server-side search across the FULL synced directory (not just the loaded page).
  useEffect(() => {
    if (!pickerOpen) return;
    const q = chatQuery.trim();
    clearTimeout(searchTimer.current);
    if (!q) { setSearchResults(null); setSearching(false); return; }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/whatsapp/chats", { params: { q, limit: 1000 } });
        setSearchResults(data.chats || []);
      } catch (e) {
        setSearchResults(null); // fall back to client-side filter
      } finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(searchTimer.current);
  }, [chatQuery, pickerOpen]);

  const relTime = (unixSec) => {
    if (!unixSec) return null;
    const diff = Date.now() / 1000 - unixSec;
    if (diff < 3600) return `il y a ${Math.max(1, Math.round(diff / 60))} min`;
    if (diff < 86400) return `il y a ${Math.round(diff / 3600)} h`;
    return `il y a ${Math.round(diff / 86400)} j`;
  };

  const digitsOnly = (s) => (s || "").replace(/\D/g, "");
  const phoneMatches = (qd, pd) => {
    if (!qd || !pd) return false;
    if (pd.includes(qd)) return true;
    if (qd.startsWith("0")) {
      const local = qd.replace(/^0+/, "");
      if (local && (pd.includes(local) || pd.endsWith(local))) return true;
    }
    return pd.endsWith(qd);
  };

  const filteredChats = chats.filter((c) => {
    const q = chatQuery.trim().toLowerCase();
    if (!q) return true;
    const qd = digitsOnly(chatQuery);
    return (c.name || "").toLowerCase().includes(q) || phoneMatches(qd, digitsOnly(c.phone));
  });
  // When searching, prefer server results (scans the whole synced directory),
  // falling back to the instant client-side filter while the request is in flight.
  const baseChats = searchResults !== null ? searchResults : filteredChats;
  const shownChats = baseChats.filter((c) => {
    if (pickerFilter === "named") return c.has_name;
    if (pickerFilter === "untracked") return c.tracking_status !== "tracked";
    return true;
  });

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-400 text-xs font-bold mb-3">
            <Sparkles size={12} /> Pro · Suivi 24/7
          </div>
          <h1 className="font-display text-3xl font-black tracking-tight">WhatsApp en direct</h1>
          <p className="text-zinc-500 text-sm mt-1">
            Le Doc veille sur ta relation en continu — chaque message met à jour le health score.
          </p>
        </div>

        {view === "loading" && (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-pink-400" /></div>
        )}

        {view === "none" && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
            <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-5 flex items-start gap-3">
              <ShieldCheck className="text-emerald-400 shrink-0" size={20} />
              <p className="text-sm text-zinc-400">
                <span className="text-zinc-200 font-semibold">Lecture seule & privé.</span> DocLove n'envoie jamais de message.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2 p-1 rounded-2xl bg-zinc-900 border border-zinc-800" data-testid="wa-method-toggle">
              <button
                data-testid="wa-method-code"
                onClick={() => { vibrate(); setMethod("code"); }}
                className={`flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold transition-colors ${method === "code" ? "bg-pink-500 text-white" : "text-zinc-400"}`}
              >
                <Smartphone size={15} /> Code (mobile)
              </button>
              <button
                data-testid="wa-method-qr"
                onClick={() => { vibrate(); setMethod("qr"); }}
                className={`flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold transition-colors ${method === "qr" ? "bg-pink-500 text-white" : "text-zinc-400"}`}
              >
                <Monitor size={15} /> QR (ordinateur)
              </button>
            </div>

            {method === "code" ? (
              <>
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Numéro WhatsApp</label>
                  <div className="flex items-center gap-2 mt-2 rounded-xl bg-zinc-950 border border-zinc-800 px-4 focus-within:border-pink-500 transition-colors">
                    <Smartphone size={16} className="text-zinc-500" />
                    <input
                      data-testid="whatsapp-phone-input"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+33 6 12 34 56 78"
                      className="flex-1 bg-transparent py-3 text-sm focus:outline-none"
                    />
                  </div>
                </div>
                <button
                  data-testid="whatsapp-connect"
                  onClick={getCode}
                  disabled={busy}
                  className="w-full flex items-center justify-center gap-2 rounded-full bg-pink-500 text-white font-bold py-4 active:scale-95 transition-transform hover:bg-pink-600 disabled:opacity-60 glow-pink"
                >
                  {busy ? <Loader2 size={18} className="animate-spin" /> : <KeyRound size={18} />} Obtenir mon code
                </button>
              </>
            ) : (
              <>
                <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-5 text-sm text-zinc-400">
                  Idéal sur ordinateur : génère un QR code et scanne-le avec l'appareil photo de WhatsApp sur ton téléphone.
                </div>
                <button
                  data-testid="whatsapp-qr-connect"
                  onClick={startQr}
                  disabled={busy}
                  className="w-full flex items-center justify-center gap-2 rounded-full bg-pink-500 text-white font-bold py-4 active:scale-95 transition-transform hover:bg-pink-600 disabled:opacity-60 glow-pink"
                >
                  {busy ? <Loader2 size={18} className="animate-spin" /> : <QrCode size={18} />} Générer le QR code
                </button>
              </>
            )}

            <button data-testid="use-paste-instead" onClick={() => navigate("/import")}
              className="w-full flex items-center justify-center gap-2 rounded-full bg-zinc-800 text-white font-semibold py-3 hover:bg-zinc-700 transition-colors">
              <ClipboardPaste size={16} /> Importer un fichier / coller du texte
            </button>
          </motion.div>
        )}

        {view === "qr" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5" data-testid="whatsapp-qr">
            <div className="rounded-2xl bg-white p-5 flex items-center justify-center aspect-square max-w-[280px] mx-auto">
              {qrUrl ? (
                <img src={qrUrl} alt="QR WhatsApp" className="w-full h-full object-contain" data-testid="qr-image" />
              ) : (
                <div className="flex flex-col items-center gap-2 text-zinc-500">
                  <Loader2 className="animate-spin" /> <span className="text-xs">Génération du QR…</span>
                </div>
              )}
            </div>
            <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-5">
              <p className="text-sm text-zinc-300 leading-relaxed">
                Ouvre <span className="text-white font-semibold">WhatsApp</span> sur ton téléphone → <span className="text-white font-semibold">Paramètres → Appareils liés → Lier un appareil</span> → scanne ce QR code.
              </p>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-zinc-500">
              <Loader2 size={15} className="animate-spin" /> En attente du scan…
            </div>
            <button data-testid="qr-back" onClick={() => { clearInterval(poll.current); setView("none"); setQrUrl(null); }}
              className="w-full text-sm text-zinc-500 hover:text-zinc-300 py-2">Retour</button>
          </motion.div>
        )}

        {view === "pending" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5" data-testid="whatsapp-pending">
            {code && (
              <div className="rounded-2xl bg-gradient-to-br from-pink-500/15 to-purple-500/15 border border-pink-500/30 p-6 text-center">
                <p className="text-xs uppercase tracking-widest text-pink-400 font-bold mb-3">Ton code d'association</p>
                <p className="font-mono text-4xl font-black tracking-[0.2em] text-white" data-testid="pairing-code">{code}</p>
              </div>
            )}
            <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-5">
              <ol className="space-y-3">
                {STEPS.map((s, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-zinc-300">
                    <span className="w-6 h-6 rounded-full bg-pink-500/15 text-pink-400 text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                    {s}
                  </li>
                ))}
              </ol>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-zinc-500">
              <Loader2 size={15} className="animate-spin" /> En attente de connexion…
            </div>
            <button data-testid="regenerate-code" onClick={getCode} className="w-full text-sm text-pink-400 font-semibold py-2">
              Générer un nouveau code
            </button>
          </motion.div>
        )}

        {view === "connected" && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-5" data-testid="whatsapp-connected">
            <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/25 p-5 flex items-center gap-3">
              <CheckCircle2 className="text-emerald-400" size={22} />
              <div className="flex-1">
                <p className="text-sm font-semibold text-white">WhatsApp connecté</p>
                <p className="text-xs text-zinc-500">{session?.phone_number}</p>
              </div>
              <button data-testid="whatsapp-disconnect" onClick={disconnect} disabled={busy}
                className="flex items-center gap-1 text-xs text-red-400 font-semibold hover:text-red-300">
                <LogOut size={14} /> Déconnecter
              </button>
            </div>

            {importing && (
              <div data-testid="wa-importing" className="rounded-2xl bg-pink-500/10 border border-pink-500/25 p-4 flex items-center gap-3">
                <Loader2 className="animate-spin text-pink-400 shrink-0" size={18} />
                <p className="text-sm text-zinc-300">Import de tes conversations récentes (3-7 derniers jours)…</p>
              </div>
            )}

            <button
              data-testid="wa-open-picker"
              onClick={openPicker}
              className="w-full flex items-center justify-center gap-2 rounded-full bg-pink-500 text-white font-bold py-3.5 active:scale-95 transition-transform hover:bg-pink-600 glow-pink"
            >
              <UserPlus size={17} /> Choisir un contact à suivre
            </button>

            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Contacts suivis</p>
                <span data-testid="tracked-count" className={`text-xs font-bold ${trackedCount >= maxTracked ? "text-amber-400" : "text-pink-400"}`}>
                  {trackedCount}/{maxTracked}
                </span>
              </div>

              {contacts.filter((c) => c.tracking_status === "tracked").length === 0 ? (
                <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-6 text-center text-sm text-zinc-500">
                  <MessageCircle className="mx-auto mb-2 text-zinc-600" size={22} />
                  Choisis jusqu'à {maxTracked} contacts à suivre avec le bouton ci-dessus.
                </div>
              ) : (
                <div className="space-y-2">
                  {contacts.filter((c) => c.tracking_status === "tracked").map((c) => (
                    <div key={c.relationship_id} data-testid={`wa-tracked-${c.relationship_id}`}
                      className="rounded-2xl bg-zinc-900 border border-pink-500/20 p-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500/40 to-purple-500/40 flex items-center justify-center text-white font-bold">
                        {(c.contact_name || "?")[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white truncate">{c.contact_name}</p>
                        <p className="text-xs text-zinc-500">{c.message_count} messages</p>
                      </div>
                      {c.health_score != null && <span className="font-display text-lg font-black text-pink-400">{c.health_score}</span>}
                      {c.needs_analysis ? (
                        <button onClick={() => analyzeContact(c.relationship_id)} disabled={analyzing === c.relationship_id}
                          data-testid={`wa-analyze-${c.relationship_id}`}
                          className="text-xs font-bold text-white bg-pink-500 rounded-full px-3 py-1.5 hover:bg-pink-600 disabled:opacity-60">
                          {analyzing === c.relationship_id ? <Loader2 size={13} className="animate-spin" /> : "Analyser"}
                        </button>
                      ) : (
                        <button onClick={() => navigate(`/relationship/${c.relationship_id}`)} className="text-zinc-500 hover:text-white">
                          <ChevronRight size={18} />
                        </button>
                      )}
                      <button data-testid={`wa-untrack-${c.relationship_id}`} onClick={() => untrackContact(c.relationship_id)}
                        title="Retirer du suivi" className="text-zinc-500 hover:text-amber-400"><Minus size={16} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              data-testid="wa-import-file"
              onClick={() => navigate("/import")}
              className="w-full flex items-center justify-center gap-2 rounded-2xl bg-zinc-900 border border-dashed border-zinc-700 hover:border-pink-500/40 text-zinc-300 text-sm font-semibold py-3.5 transition-colors"
            >
              <ClipboardPaste size={15} /> Historique incomplet ? Importer le fichier .txt d'une conversation
            </button>
          </motion.div>
        )}

        {view === "expired" && (
          <div className="rounded-2xl bg-amber-500/10 border border-amber-500/25 p-6 text-center space-y-4" data-testid="whatsapp-expired">
            <Clock className="mx-auto text-amber-400" size={26} />
            <p className="text-sm text-zinc-300">Ta session a expiré. Reconnecte-toi pour reprendre le suivi.</p>
            <button data-testid="reconnect" onClick={() => { setView("none"); setCode(null); }}
              className="rounded-full bg-pink-500 text-white font-bold py-3 px-6 hover:bg-pink-600">Se reconnecter</button>
          </div>
        )}

        {view === "soon" && (
          <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-6 text-center space-y-4" data-testid="whatsapp-soon">
            <Sparkles className="mx-auto text-pink-400" size={26} />
            <div>
              <p className="font-display text-lg font-bold">Suivi WhatsApp en cours d'activation</p>
              <p className="text-sm text-zinc-500 mt-1">La connexion directe s'active très bientôt. En attendant, importe un fichier ou colle ta conv.</p>
            </div>
            <button data-testid="soon-goto-import" onClick={() => navigate("/import")}
              className="rounded-full bg-pink-500 text-white font-bold py-3 px-6 hover:bg-pink-600">Importer autrement</button>
          </div>
        )}
      </div>

      {paywall && <PaywallModal data={paywall} onClose={() => setPaywall(null)} />}

      {pickerOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="wa-chat-picker">
          <motion.div
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
            className="w-full sm:max-w-lg bg-zinc-950 border border-zinc-800 rounded-t-3xl sm:rounded-3xl max-h-[85vh] flex flex-col"
          >
            <div className="flex items-center justify-between p-5 border-b border-zinc-800">
              <div>
                <h2 className="font-display text-lg font-black text-white">Choisir un contact</h2>
                <p className="text-xs text-zinc-500 mt-0.5">Suivis {trackedCount}/{maxTracked}</p>
              </div>
              <button data-testid="wa-picker-close" onClick={() => setPickerOpen(false)} className="text-zinc-500 hover:text-white p-1">
                <X size={22} />
              </button>
            </div>

            <div className="p-4 border-b border-zinc-800">
              <div className="flex items-center gap-2 rounded-xl bg-zinc-900 border border-zinc-800 px-3 focus-within:border-pink-500 transition-colors">
                <Search size={16} className="text-zinc-500" />
                <input
                  data-testid="wa-picker-search"
                  value={chatQuery}
                  onChange={(e) => setChatQuery(e.target.value)}
                  placeholder="Rechercher un nom ou un numéro…"
                  className="flex-1 bg-transparent py-2.5 text-sm focus:outline-none"
                />
              </div>
              <div className="flex gap-2 mt-3" data-testid="wa-picker-filters">
                {[["all", "Tous"], ["named", "Avec nom"], ["untracked", "Non suivis"]].map(([k, label]) => (
                  <button
                    key={k}
                    data-testid={`wa-filter-${k}`}
                    onClick={() => setPickerFilter(k)}
                    className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ${
                      pickerFilter === k
                        ? "bg-pink-500 text-white border-pink-500"
                        : "text-zinc-400 border-zinc-700 hover:border-zinc-500"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {chatsLoading ? (
                <div className="flex justify-center py-12"><Loader2 className="animate-spin text-pink-400" /></div>
              ) : searching ? (
                <div className="flex items-center justify-center gap-2 py-12 text-sm text-zinc-500"><Loader2 className="animate-spin text-pink-400" size={16} /> Recherche…</div>
              ) : shownChats.length === 0 ? (
                <div className="text-center py-10 text-sm text-zinc-500" data-testid="wa-chats-empty">
                  <MessageCircle className="mx-auto mb-2 text-zinc-600" size={22} />
                  {chatQuery.trim()
                    ? "Aucun résultat. WhatsApp ne remonte que les conversations actives récemment — pour un contact plus ancien, importe le fichier .txt ci-dessous."
                    : (chats.length === 0
                        ? "Aucune conversation détectée pour l'instant. WhatsApp ne remonte que les chats actifs depuis la connexion."
                        : "Aucun résultat pour cette recherche.")}
                </div>
              ) : (
                shownChats.map((c) => {
                  const isTracked = c.tracking_status === "tracked";
                  return (
                    <div key={c.jid} data-testid={`wa-chat-${c.jid}`}
                      className="rounded-2xl bg-zinc-900 border border-zinc-800 p-3.5 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500/40 to-purple-500/40 flex items-center justify-center text-white font-bold shrink-0">
                        {(c.name || "?")[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white truncate flex items-center gap-1.5">
                          {c.name}
                          {c.unread_count > 0 && (
                            <span className="text-[9px] font-bold text-white bg-pink-500 rounded-full px-1.5 py-0.5 shrink-0">{c.unread_count}</span>
                          )}
                        </p>
                        <p className="text-xs text-zinc-500 truncate">
                          {c.last_message_preview || (c.has_name ? c.phone : "")}
                          {relTime(c.last_message_at) && <span className="text-zinc-600"> · {relTime(c.last_message_at)}</span>}
                          {c.message_count > 0 && <span className="text-zinc-600"> · {c.message_count} msg captés</span>}
                        </p>
                      </div>
                      {isTracked ? (
                        <span className="flex items-center gap-1 text-xs font-bold text-emerald-400 shrink-0">
                          <CheckCircle2 size={14} /> Suivi
                        </span>
                      ) : (
                        <button
                          data-testid={`wa-pick-${c.jid}`}
                          onClick={() => pickChat(c)}
                          disabled={trackingJid === c.jid || trackedCount >= maxTracked}
                          className="flex items-center gap-1 text-xs font-bold text-pink-400 border border-pink-500/30 rounded-full px-3 py-1.5 hover:bg-pink-500/10 disabled:opacity-50 shrink-0"
                        >
                          {trackingJid === c.jid ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Suivre
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            <div className="p-4 border-t border-zinc-800">
              <button
                data-testid="wa-picker-import-file"
                onClick={() => { setPickerOpen(false); navigate("/import"); }}
                className="w-full flex items-center justify-center gap-2 rounded-xl bg-zinc-900 border border-dashed border-zinc-700 hover:border-pink-500/40 text-zinc-300 text-sm font-semibold py-3 transition-colors"
              >
                <ClipboardPaste size={15} /> Contact absent ? Importer un fichier .txt
              </button>
              <p className="text-[11px] text-zinc-600 text-center mt-2">
                WhatsApp ne remonte que les chats actifs récemment. Pour un contact dormant, utilise l'import de fichier.
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </Layout>
  );
}
